import { useState } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { Card, CardHeader, Button, Input, Alert, Spinner } from '../../components/UI';
import { useClients } from '../../hooks/useClients';
import { useAssessment } from '../../hooks/useAssessment';
import { Client } from '../../types';
import { PlusIcon, PlayIcon, DocumentArrowDownIcon, LightBulbIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline';

// Format currency in USD format (comma for thousands, dot for decimals)
const formatCurrency = (value: number): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const CLOUDFORMATION_TEMPLATE = `AWSTemplateFormatVersion: '2010-09-09'
Description: |
  Rol IAM para Honne Cost Optimization Assessment.
  Este rol permite a Honne realizar un assessment de optimizacion de costos en su cuenta AWS.
  El rol tiene permisos de SOLO LECTURA y no puede modificar ningun recurso.

Resources:
  HonneCostOptimizationRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: HonneCostOptimizationRole
      Description: Rol de solo lectura para Honne Cost Optimization Assessment
      MaxSessionDuration: 3600
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              AWS: 'arn:aws:iam::860217763699:root'
            Action: sts:AssumeRole
      Policies:
        - PolicyName: CostExplorerReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - ce:GetCostAndUsage
                  - ce:GetCostForecast
                  - ce:GetSavingsPlansUtilization
                  - ce:GetSavingsPlansCoverage
                  - ce:GetSavingsPlansUtilizationDetails
                  - ce:GetSavingsPlansPurchaseRecommendation
                  - ce:GetReservationUtilization
                  - ce:GetReservationCoverage
                  - ce:GetRightsizingRecommendation
                Resource: '*'
        - PolicyName: EC2ReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - ec2:DescribeInstances
                  - ec2:DescribeVolumes
                  - ec2:DescribeAddresses
                  - ec2:DescribeSnapshots
                  - ec2:DescribeRegions
                  - ec2:DescribeAvailabilityZones
                  - ec2:DescribeInstanceTypes
                  - ec2:DescribeInstanceStatus
                  - ec2:DescribeNatGateways
                Resource: '*'
        - PolicyName: RDSReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - rds:DescribeDBInstances
                  - rds:DescribeDBClusters
                  - rds:DescribeReservedDBInstances
                  - rds:ListTagsForResource
                Resource: '*'
        - PolicyName: S3ReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - s3:ListAllMyBuckets
                  - s3:GetBucketLocation
                  - s3:GetBucketTagging
                Resource: '*'
        - PolicyName: ELBReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - elasticloadbalancing:DescribeLoadBalancers
                  - elasticloadbalancing:DescribeTargetGroups
                  - elasticloadbalancing:DescribeTargetHealth
                  - elasticloadbalancing:DescribeListeners
                  - elasticloadbalancing:DescribeTags
                Resource: '*'
        - PolicyName: CloudWatchReadAccess
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action:
                  - cloudwatch:GetMetricStatistics
                  - cloudwatch:GetMetricData
                  - cloudwatch:ListMetrics
                  - logs:DescribeLogGroups
                Resource: '*'
      Tags:
        - Key: Purpose
          Value: Honne Cost Optimization Assessment
        - Key: CreatedBy
          Value: Honne CloudFormation Template
        - Key: AccessType
          Value: ReadOnly

Outputs:
  RoleArn:
    Description: ARN del rol - Proporcione este valor a su ejecutivo de Honne
    Value: !GetAtt HonneCostOptimizationRole.Arn
  NextSteps:
    Description: Siguientes pasos
    Value: |
      1. Copie el ARN del rol de la salida 'RoleArn'
      2. Envie el ARN a su ejecutivo de ventas de Honne
`;

export function DashboardPage() {
  const { clients, loading: clientsLoading, createClient, deleteClient } = useClients();
  const { status, results, strategies, roleArn: assessmentRoleArn, loading: assessmentLoading, error: assessmentError, startAssessment, reset } = useAssessment();
  
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [roleArn, setRoleArn] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [arnError, setArnError] = useState('');

  // Extract Account ID from roleArn (format: arn:aws:iam::123456789012:role/RoleName)
  const getAccountIdFromArn = (arn?: string | null): string | null => {
    if (!arn) return null;
    const parts = arn.split(':');
    return parts.length >= 5 ? parts[4] : null;
  };

  const accountId = getAccountIdFromArn(assessmentRoleArn);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CLOUDFORMATION_TEMPLATE], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'honne-cost-optimization-role.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    await createClient({ name: newClientName.trim() });
    setNewClientName('');
    setShowNewClient(false);
  };

  const validateArn = (arn: string): boolean => {
    const arnRegex = /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/;
    return arnRegex.test(arn);
  };

  const handleStartAssessment = async () => {
    if (!selectedClient || !roleArn) return;
    
    if (!validateArn(roleArn)) {
      setArnError('Formato de ARN inválido. Ejemplo: arn:aws:iam::123456789012:role/CostOptimizationRole');
      return;
    }
    
    setArnError('');
    await startAssessment({ clientId: selectedClient.id, roleArn, region });
  };

  const handleNewAssessment = () => {
    reset();
    setSelectedClient(null);
    setRoleArn('');
    setRegion('us-east-1');
  };

  // Show results if assessment is complete
  if (results && status?.status === 'COMPLETED') {
    return (
      <Layout>
        <div className="flex gap-6">
          {/* Main Results Column */}
          <div className="flex-1 space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-honne-navy">Resultados del Assessment</h2>
                {accountId && (
                  <p className="text-sm text-gray-600 mt-1">AWS Account: {accountId}</p>
                )}
              </div>
              <div className="flex gap-3">
                {status.reportUrl && (
                  <a
                    href={status.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-sm bg-gradient-accent text-white hover:shadow-lg hover:shadow-accent-500/30 transition-all"
                    data-testid="download-report-button"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Ver Reporte
                  </a>
                )}
                <Button variant="secondary" onClick={handleNewAssessment} data-testid="new-assessment-button">
                  Nuevo Assessment
                </Button>
              </div>
            </div>

            {/* Savings Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <div className="text-center">
                  <p className="text-sm text-green-700 font-medium">Ahorro Mensual Potencial</p>
                  <p className="text-4xl font-bold text-green-600 mt-2">
                    ${formatCurrency(results.savingsSummary.totalMonthlySavings)}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Costo Proyectado Mes Actual</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    ${formatCurrency(results.costSummary.totalCost)}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Costo Mes Anterior</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    ${formatCurrency(results.costSummary.previousMonthCost)}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Recomendaciones</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {results.recommendations.length}
                  </p>
                </div>
              </Card>
            </div>

            {/* Savings by Category */}
            <Card>
              <CardHeader title="Ahorro por Categoría" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-red-600 font-medium">Recursos Ociosos</p>
                      <p className="text-2xl font-bold text-red-700">
                        ${formatCurrency(results.savingsSummary.byCategory['IDLE_RESOURCES'] || 0)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">RightSizing</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        ${formatCurrency(results.savingsSummary.byCategory['RIGHTSIZING'] || 0)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Savings Plans</p>
                      <p className="text-2xl font-bold text-blue-700">
                        ${formatCurrency(results.savingsSummary.byCategory['SAVINGS_PLANS'] || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader title="Recomendaciones de Optimización" />
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-honne-light">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Categoría</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Descripción</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Ahorro/mes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-honne-navy uppercase">Prioridad</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results.recommendations.map((rec) => (
                      <tr key={rec.id} className="hover:bg-honne-light/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            rec.category === 'IDLE_RESOURCES' ? 'bg-red-100 text-red-800' :
                            rec.category === 'RIGHTSIZING' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-accent-100 text-accent-800'
                          }`}>
                            {rec.category.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{rec.description}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-accent-600">
                          ${formatCurrency(rec.estimatedMonthlySavings)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`font-medium ${
                            rec.priority === 'HIGH' ? 'text-red-600' :
                            rec.priority === 'MEDIUM' ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {rec.priority}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Sales Strategies Sidebar */}
          <div className="w-96 flex-shrink-0">
            <Card className="sticky top-6 bg-gradient-to-br from-honne-navy to-honne-blue text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <LightBulbIcon className="h-6 w-6 text-accent-400" />
                </div>
                <h3 className="text-lg font-bold">Estrategias de Venta</h3>
              </div>
              
              {strategies && strategies.length > 0 ? (
                <div className="space-y-4">
                  {strategies.map((strategy, index) => (
                    <div key={index} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <h4 className="font-semibold text-accent-300 mb-2">{strategy.title}</h4>
                      <p className="text-sm text-gray-200 mb-3">{strategy.description}</p>
                      
                      <div className="bg-white/10 rounded-lg p-3 mb-3">
                        <p className="text-xs text-gray-300 uppercase tracking-wide mb-1">Precio Sugerido</p>
                        <p className="text-sm font-medium text-accent-300">{strategy.pricing}</p>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-xs text-gray-300 uppercase tracking-wide mb-2">Beneficios Clave</p>
                        <ul className="space-y-1">
                          {strategy.benefits.map((benefit, i) => (
                            <li key={i} className="text-sm text-gray-200 flex items-start gap-2">
                              <span className="text-accent-400 mt-0.5">✓</span>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="border-t border-white/20 pt-3">
                        <p className="text-sm italic text-accent-200">"{strategy.closingPhrase}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Spinner className="mx-auto mb-3" />
                  <p className="text-sm text-gray-300">Generando estrategias con IA...</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Show progress if assessment is running
  if (status && (status.status === 'RUNNING' || status.status === 'PENDING')) {
    return (
      <Layout>
        <Card className="max-w-2xl mx-auto text-center py-12">
          <Spinner size="lg" className="mx-auto mb-6" />
          <h2 className="text-xl font-semibold text-honne-navy mb-2">
            Assessment en Progreso
          </h2>
          <p className="text-gray-600 mb-6">
            {status.currentStep.replace(/_/g, ' ')}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2 overflow-hidden">
            <div
              className="bg-gradient-accent h-4 rounded-full transition-all duration-500"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">{status.progress}% completado</p>
        </Card>
      </Layout>
    );
  }

  // Show error if assessment failed
  if (status && status.status === 'FAILED') {
    // Parse error message - it might be a JSON string from Step Functions
    let errorMessage = status.error?.message || '';
    let errorDetails = '';
    
    try {
      // Try to parse as JSON (Step Functions error format)
      const parsed = JSON.parse(errorMessage);
      errorMessage = parsed.errorMessage || errorMessage;
      errorDetails = parsed.errorType || '';
    } catch {
      // Not JSON, use as-is
    }
    
    const isSCPError = errorMessage.includes('service control policy') || errorMessage.includes('explicit deny');
    const isInternalError = errorMessage.includes('Cannot read properties') || errorMessage.includes('undefined');
    
    return (
      <Layout>
        <Card className="max-w-3xl mx-auto py-12">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-600 mb-2">
              Assessment Fallido
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Paso donde falló: {status.currentStep.replace(/_/g, ' ')}
            </p>
          </div>

          {isSCPError ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-800 mb-2">
                    Service Control Policy (SCP) bloqueando acceso
                  </h3>
                  <p className="text-sm text-yellow-700 mb-3">
                    La cuenta AWS tiene una Service Control Policy de AWS Organizations que está bloqueando el acceso a Cost Explorer.
                  </p>
                  <div className="bg-white rounded p-3 mb-3">
                    <p className="text-xs font-mono text-gray-600 break-all">
                      {errorMessage}
                    </p>
                  </div>
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-2">Solución requerida:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Contactar al administrador de AWS Organizations</li>
                      <li>Solicitar modificación de la SCP para permitir acceso a Cost Explorer (ce:*)</li>
                      <li>O crear una excepción para el rol HonneCostOptimizationRole</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          ) : isInternalError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-2">
                    Error interno del sistema
                  </h3>
                  <p className="text-sm text-red-700 mb-3">
                    Ocurrió un error inesperado durante el análisis. Nuestro equipo ha sido notificado.
                  </p>
                  <div className="bg-white rounded p-3 mb-3">
                    <p className="text-xs text-gray-500 mb-1">Detalles técnicos:</p>
                    <p className="text-xs font-mono text-gray-600 break-all">
                      {errorDetails && <span className="text-red-600">[{errorDetails}]</span>} {errorMessage}
                    </p>
                  </div>
                  <p className="text-sm text-red-700">
                    Por favor, intenta nuevamente. Si el problema persiste, contacta a soporte.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <p className="text-gray-700 mb-3">
                Hubo un error al ejecutar el assessment. Por favor, verifica:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 ml-2">
                <li>El rol IAM esté configurado correctamente</li>
                <li>El rol tenga los permisos necesarios (Cost Explorer, EC2, RDS, etc.)</li>
                <li>La política de confianza permita asumir el rol desde nuestra cuenta</li>
                <li>No haya Service Control Policies bloqueando el acceso</li>
              </ul>
              {errorMessage && (
                <div className="mt-4 bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">Detalles del error:</p>
                  <p className="text-xs font-mono text-gray-600 break-all">
                    {errorMessage}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="text-center">
            <Button onClick={handleNewAssessment}>
              Intentar Nuevamente
            </Button>
          </div>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Clients Section */}
        <Card>
          <CardHeader
            title="Clientes"
            action={
              <Button size="sm" onClick={() => setShowNewClient(true)} data-testid="add-client-button">
                <PlusIcon className="h-4 w-4 mr-1" />
                Nuevo
              </Button>
            }
          />

          {showNewClient && (
            <div className="mb-4 p-4 bg-honne-light rounded-xl">
              <Input
                label="Nombre del cliente"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Ej: Empresa ABC"
                data-testid="new-client-name-input"
              />
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleCreateClient} data-testid="save-client-button">
                  Guardar
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setShowNewClient(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {clientsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : clients.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No hay clientes registrados
            </p>
          ) : (
            <div className="space-y-2">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    selectedClient?.id === client.id
                      ? 'border-accent-500 bg-accent-50 shadow-md'
                      : 'border-gray-100 hover:border-honne-blue/30 hover:shadow-sm'
                  }`}
                  onClick={() => setSelectedClient(client)}
                  data-testid={`client-card-${client.id}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-honne-navy">{client.name}</p>
                      <p className="text-sm text-gray-500">
                        {client.assessmentCount} assessments
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteClient(client.id);
                      }}
                      className="text-gray-400 hover:text-red-500 text-xl font-bold"
                      aria-label={`Eliminar ${client.name}`}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Assessment Section */}
        <Card>
          <CardHeader title="Iniciar Assessment" />

          {/* Download CloudFormation Template */}
          <div className="mb-6 p-4 bg-gradient-to-r from-honne-light to-blue-50 rounded-xl border border-honne-blue/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-honne-blue/10 flex items-center justify-center flex-shrink-0">
                <CloudArrowDownIcon className="h-5 w-5 text-honne-blue" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-honne-navy mb-1">¿El cliente no tiene el rol configurado?</p>
                <p className="text-sm text-gray-600 mb-3">
                  Descarga el template de CloudFormation y compártelo con tu cliente para que cree el rol necesario en su cuenta AWS.
                </p>
                <Button
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="bg-accent-500 hover:bg-honne-blue text-white"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Descargar archivo AWS
                </Button>
              </div>
            </div>
          </div>

          {assessmentError && (
            <Alert type="error" message={assessmentError} className="mb-4" />
          )}

          {selectedClient ? (
            <div className="space-y-4">
              <div className="p-4 bg-honne-light rounded-xl border border-honne-blue/10">
                <p className="text-sm text-gray-500">Cliente seleccionado</p>
                <p className="font-semibold text-honne-navy">{selectedClient.name}</p>
              </div>

              <Input
                label="ARN del Rol IAM"
                value={roleArn}
                onChange={(e) => {
                  setRoleArn(e.target.value);
                  setArnError('');
                }}
                placeholder="arn:aws:iam::123456789012:role/CostOptimizationRole"
                error={arnError}
                helperText="El rol debe tener permisos de lectura para Cost Explorer, EC2, RDS, S3, etc."
                data-testid="role-arn-input"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Región AWS Principal
                </label>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                  data-testid="region-select"
                >
                  <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                  <option value="us-east-2">US East (Ohio) - us-east-2</option>
                  <option value="us-west-1">US West (N. California) - us-west-1</option>
                  <option value="us-west-2">US West (Oregon) - us-west-2</option>
                  <option value="ca-central-1">Canada (Central) - ca-central-1</option>
                  <option value="sa-east-1">South America (São Paulo) - sa-east-1</option>
                  <option value="mx-central-1">Mexico (Central) - mx-central-1</option>
                  <option value="eu-west-1">Europe (Ireland) - eu-west-1</option>
                  <option value="eu-west-2">Europe (London) - eu-west-2</option>
                  <option value="eu-west-3">Europe (Paris) - eu-west-3</option>
                  <option value="eu-central-1">Europe (Frankfurt) - eu-central-1</option>
                  <option value="eu-north-1">Europe (Stockholm) - eu-north-1</option>
                  <option value="ap-south-1">Asia Pacific (Mumbai) - ap-south-1</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</option>
                  <option value="ap-northeast-2">Asia Pacific (Seoul) - ap-northeast-2</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                  <option value="ap-southeast-2">Asia Pacific (Sydney) - ap-southeast-2</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona la región donde el cliente tiene la mayoría de sus recursos
                </p>
              </div>

              <Button
                onClick={handleStartAssessment}
                loading={assessmentLoading}
                disabled={!roleArn}
                className="w-full"
                data-testid="start-assessment-button"
              >
                <PlayIcon className="h-5 w-5 mr-2" />
                Iniciar Assessment (30 min)
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-honne-light flex items-center justify-center">
                <PlayIcon className="h-8 w-8 text-honne-blue" />
              </div>
              <p>Selecciona un cliente para iniciar un assessment</p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
